import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { alertMonitoringApi, parseAlertHistoryResult, pruneEmptyParams } from '../api/adminAlert.api';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import XmitCell from '../components/users/XmitCell';
import DeleteAlertModal from '../components/alerts/DeleteAlertModal';
import ToastBanner from '../components/ToastBanner';
import { usePagination } from '../hooks/usePagination';
import { apiHandler } from '../utils/apiHandler';

function AlertStatusBadge({ status }) {
  const tones = {
    triggered: { bg: '#FEE2E2', color: '#B91C1C' },
    active: { bg: '#FFEDD5', color: '#C2410C' },
    escalated: { bg: '#EDE9FE', color: '#6D28D9' },
    acknowledged: { bg: '#DBEAFE', color: '#1D4ED8' },
    resolved: { bg: '#DCFCE7', color: '#15803D' },
    cancelled: { bg: '#F1F5F9', color: '#475569' },
    false_alarm: { bg: '#FEF3C7', color: '#B45309' },
  };
  const t = tones[status] || tones.cancelled;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        padding: '3px 9px',
        borderRadius: 99,
        textTransform: 'capitalize',
      }}
    >
      {(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

const ALERT_TYPE_OPTIONS = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'medical', label: 'Medical' },
  { value: 'roadside', label: 'Roadside' },
  { value: 'trusted_circle', label: 'Trusted circle' },
  { value: 'urgent_assist', label: 'Urgent assist' },
  { value: 'custom', label: 'Custom' },
  { value: 'false_alarm', label: 'False alarm' },
  { value: 'suspicious', label: 'Suspicious' },
];

const AlertHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userIdFilter = searchParams.get('userId') || '';

  const [alerts, setAlerts] = useState([]);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const {
    page,
    limit,
    setPage,
    setLoading,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  const fetchHistory = useCallback(
    async (pageNum = page) => {
      setLoading(true);
      setFetchError('');
      const params = pruneEmptyParams({
        page: pageNum,
        limit,
        ...filters,
        ...(userIdFilter ? { userId: userIdFilter } : {}),
      });

      const result = await apiHandler(() => alertMonitoringApi.getHistory(params));
      const { rows, pagination, error } = parseAlertHistoryResult(result);
      if (error) {
        setFetchError(error);
        setAlerts([]);
      } else {
        setAlerts(rows);
        applyPaginationMeta({
          total: pagination.total,
          page: pagination.page,
          totalPages: pagination.pages,
        });
      }
      setLoading(false);
    },
    [page, limit, filters, userIdFilter, applyPaginationMeta, setLoading]
  );

  useEffect(() => {
    if (location.state?.toast) {
      setToast({ msg: location.state.toast, type: location.state.toastType || 'success' });
      navigate(location.pathname + location.search, { replace: true, state: {} });
      const timer = setTimeout(() => setToast(null), 3200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    fetchHistory(page);
  }, [page, filters, userIdFilter, fetchHistory]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  async function handleConfirmDelete(confirmation) {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const result = await apiHandler(() =>
      alertMonitoringApi.deleteAlert(deleteTarget.id, confirmation)
    );
    setDeleteLoading(false);
    if (result.success) {
      setDeleteTarget(null);
      fetchHistory(page);
    }
  }

  return (
    <>
      {toast && <ToastBanner toast={toast} />}

      <PageHeader
        title="Alert History"
        icon="fa-clock-rotate-left"
        trail={[{ label: 'Alerts' }, { label: 'History' }]}
      />

      {userIdFilter && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: 10,
            fontSize: 13,
            color: '#1E40AF',
          }}
        >
          Filtered to user <strong>{userIdFilter}</strong>
          {' · '}
          <button
            type="button"
            onClick={() => navigate('/alerts/history')}
            style={{ background: 'none', border: 'none', color: '#074f78', cursor: 'pointer', fontWeight: 600 }}
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="">All statuses</option>
              <option value="triggered">Triggered</option>
              <option value="active">Active</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="">All types</option>
              {ALERT_TYPE_OPTIONS.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search user</label>
            <input
              type="text"
              name="search"
              placeholder="Name or email…"
              value={filters.search}
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full border-gray-300 rounded-md shadow-sm text-sm"
            />
          </div>
        </div>
      </div>

      {fetchError && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            fontSize: 13,
            color: '#B91C1C',
          }}
        >
          {fetchError}
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>XMIT</th>
                <th>Type</th>
                <th>Status</th>
                <th>Message</th>
                <th>Contacts notified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginationProps.loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                    Loading alerts…
                  </td>
                </tr>
              ) : alerts.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                    {fetchError ? 'Could not load alerts.' : 'No alerts match your filters.'}
                  </td>
                </tr>
              ) : (
                alerts.map((alert) => (
                  <tr
                    key={alert.id}
                    onClick={() => navigate(`/alerts/${alert.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ fontSize: 12, color: '#64748B' }}>
                      {new Date(alert.createdAt).toLocaleString()}
                    </td>
                    <td>
                      {alert.user ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {alert.user.firstName} {alert.user.lastName}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{alert.user.email}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#94A3B8' }}>Unknown</span>
                      )}
                    </td>
                    <td>
                      <XmitCell
                        quickResponse={{
                          xmitNumber: alert.user?.xmitNumber,
                          xmitVerifiedAt: alert.user?.xmitVerifiedAt,
                        }}
                      />
                    </td>
                    <td style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                      {alert.type?.replace(/_/g, ' ') || '—'}
                    </td>
                    <td>
                      <AlertStatusBadge status={alert.status} />
                    </td>
                    <td style={{ fontSize: 12, color: '#334155', maxWidth: 200 }}>
                      {alert.message
                        ? `${alert.message.slice(0, 80)}${alert.message.length > 80 ? '…' : ''}`
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>
                      {alert.recipientSummary?.total ?? 0}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(alert)}
                        style={{
                          fontSize: 12,
                          color: '#B91C1C',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination {...paginationProps} />
      </div>

      <DeleteAlertModal
        alert={deleteTarget}
        open={Boolean(deleteTarget)}
        loading={deleteLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default AlertHistory;
